"use client";

import Link from "@/components/ui/NavLink";
import ServiceLink from "@/components/ui/ServiceLink";
import Image from "next/image";
import {
  InstagramIcon,
  FacebookIcon,
  LinkedinIcon,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Megaphone,
  PenTool,
  MonitorSmartphone,
  Building2,
  type LucideIcon,
} from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { openCookieSettings } from "@/components/CookieConsent";
import { services, type ServiceCategory } from "@/data/services";

const PHONE_DISPLAY = "+359 886 66 20 20";
const PHONE_HREF = "tel:+359886662020";
const EMAIL = "office@avivadigital.bg";

const CATEGORIES: { key: ServiceCategory; icon: LucideIcon }[] = [
  { key: "marketing", icon: Megaphone },
  { key: "creative", icon: PenTool },
  { key: "web", icon: MonitorSmartphone },
];

const socialLinks = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/aviva.digital",
    icon: InstagramIcon,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/avivadigital.bg",
    icon: FacebookIcon,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/avivadigital/",
    icon: LinkedinIcon,
  },
];

const headingClass =
  "font-display font-semibold text-white/60 text-xs uppercase tracking-[0.16em] mb-3 md:mb-5";
const linkClass =
  "text-white/55 hover:text-white text-sm leading-snug transition-colors duration-150";

export default function Footer() {
  const { t, locale } = useLang();

  const serviceTitle = (id: string) =>
    t.servicesList.find((s) => s.id === id)?.title ?? id;

  // SEO appears under both Marketing and Web — list it once.
  const seen = new Set<string>();
  const columns = CATEGORIES.map(({ key, icon }) => ({
    key,
    icon,
    label: t.serviceDropdown.categories[key],
    items: services
      .filter((s) => s.category === key)
      .map((s) => ({ id: s.id, title: serviceTitle(s.id) }))
      .filter((s) => (seen.has(s.title) ? false : (seen.add(s.title), true))),
  }));

  const companyLinks = [
    { label: t.nav.home, href: "/" },
    { label: t.nav.services, href: "/services" },
    { label: t.nav.websites, href: "/work/websites" },
    { label: t.nav.digitalMarketing, href: "/work/digital-marketing" },
    { label: t.nav.clients, href: "/clients" },
    { label: t.nav.about, href: "/about" },
    { label: t.faq.nav, href: "/faq" },
    { label: t.nav.contact, href: "/contact" },
  ];

  return (
    <footer className="relative bg-[#07070C] overflow-hidden">
      {/* Top gradient separator */}
      <div
        className="h-px w-full"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(224,64,160,0.7), rgba(155,89,245,0.7), rgba(74,158,255,0.5), transparent)",
        }}
      />

      {/* Background image */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none select-none"
        style={{
          backgroundImage: "url('/background-images/floating-wave-purple.webp?v=3')",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          opacity: 0.15,
        }}
      />

      {/* Ambient gradients — no blur filter */}
      <div
        aria-hidden
        className="absolute top-0 left-1/4 -translate-x-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse, rgba(224,64,160,0.11), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-1/4 translate-x-1/2 w-[500px] h-[350px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse, rgba(155,89,245,0.10), transparent 70%)",
        }}
      />

      <div className="container relative z-10">
        <div className="py-10 sm:py-14 md:py-16 3xl:py-20 4xl:py-28 grid gap-8 md:gap-12 lg:grid-cols-12 lg:gap-10">
          {/* ── Brand, contact, social ── */}
          <div className="lg:col-span-4 xl:col-span-3 flex flex-col gap-5 md:gap-6">
            <Link href="/" className="w-fit">
              <Image
                src="/aviva-digital-white-logo.webp"
                alt="Aviva Digital"
                width={160}
                height={96}
                className="h-14 sm:h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>

            <div>
              <h3 className={headingClass}>{t.footer.contactHeading}</h3>
              <ul className="space-y-2.5 md:space-y-3">
                <li>
                  <a href={`mailto:${EMAIL}`} className={`${linkClass} inline-flex items-center gap-2.5`}>
                    <Mail size={15} className="text-white/70 shrink-0" />
                    {EMAIL}
                  </a>
                </li>
                <li>
                  <a href={PHONE_HREF} className={`${linkClass} inline-flex items-center gap-2.5`}>
                    <Phone size={15} className="text-white/70 shrink-0" />
                    {PHONE_DISPLAY}
                  </a>
                </li>
                <li className="inline-flex items-center gap-2.5 text-white/55 text-sm">
                  <MapPin size={15} className="text-white/70 shrink-0" />
                  {t.contactForm.location}
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 lg:flex-col lg:items-start lg:gap-6">
            <div className="flex items-center gap-2">
              {socialLinks.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="group w-9 h-9 rounded-xl flex items-center justify-center border border-[rgba(224,64,160,0.3)] bg-[rgba(224,64,160,0.12)] hover:border-[rgba(224,64,160,0.5)] hover:bg-[rgba(224,64,160,0.22)] transition-colors duration-200"
                >
                  <Icon
                    size={14}
                    className="text-white/80 group-hover:text-white transition-colors duration-200"
                  />
                </a>
              ))}
            </div>

            <Link
              href="/contact"
              className="group inline-flex w-fit items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-sm text-white/85 hover:text-white hover:border-white/30 hover:bg-white/[0.08] transition-colors duration-200"
            >
              {t.footer.startProject}
              <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            </div>
          </div>

          {/* ── Link columns ── */}
          <div className="lg:col-span-8 xl:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-x-6 sm:gap-x-10 gap-y-10">
            {columns.map((col) => (
              <div key={col.key} className="hidden md:block">
                <h3 className={`${headingClass} flex items-center gap-2`}>
                  <col.icon size={14} className="text-white shrink-0" />
                  {col.label}
                </h3>
                <ul className="space-y-2.5">
                  {col.items.map((s) => (
                    <li key={s.id}>
                      <ServiceLink serviceId={s.id} landing className={linkClass}>
                        {s.title}
                      </ServiceLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="col-span-2 md:col-span-1">
              <h3 className={`${headingClass} flex items-center gap-2`}>
                <Building2 size={14} className="text-white shrink-0" />
                {t.footer.company}
              </h3>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 md:block md:space-y-2.5">
                {companyLinks.map((l) => (
                  <li key={l.href}>
                    <Link href={l.href} className={linkClass}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                href="/services"
                className="mt-4 md:mt-6 inline-block text-sm text-white/80 hover:text-white transition-colors duration-150"
              >
                {t.footer.allServices}
              </Link>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div
          className="py-5 md:py-6 flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:gap-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          <p className="w-full sm:w-auto text-white/50 text-xs sm:text-sm text-center">
            © {new Date().getFullYear()} Aviva Digital · avivadigital.bg ·{" "}
            {t.footer.copyright}
          </p>
          <span className="hidden sm:block text-white/20 text-sm">·</span>
          <Link
            href="/terms"
            className="text-white/35 hover:text-white/70 text-xs sm:text-sm transition-colors duration-150"
          >
            {locale === "bg" ? "Общи условия" : "Terms & Conditions"}
          </Link>
          <span className="hidden sm:block text-white/20 text-sm">·</span>
          <Link
            href="/privacy"
            className="text-white/35 hover:text-white/70 text-xs sm:text-sm transition-colors duration-150"
          >
            {t.cookies.policyLink}
          </Link>
          <span className="hidden sm:block text-white/20 text-sm">·</span>
          <button
            type="button"
            onClick={openCookieSettings}
            className="text-white/35 hover:text-white/70 text-xs sm:text-sm transition-colors duration-150 cursor-pointer"
          >
            {t.cookies.settings}
          </button>
        </div>
      </div>
    </footer>
  );
}
